// Application Deploy/Build API
// Extracted from applicationApi.ts — deploy and build-related API calls.

import {
  ApplicationEvents,
  ApplicationEndpoints,
} from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EventBus } from "src/platform/events/EventBus";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";

import type { ApplicationEventPayload } from "../types/application.types";

export async function deployApplication(
  eventBus: EventBus,
  alias: string,
  targetEnvironmentSlug: string,
  images?: Record<string, string>,
): Promise<{ id: string; status: string }> {
  try {
    if (!targetEnvironmentSlug) {
      throw new Error(
        "A target environment is required to deploy an application.",
      );
    }
    debugLogger.info("application-api", `Deploying application: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.DEPLOY_APPLICATION.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const body: Record<string, unknown> = { targetEnvironmentSlug };
    if (images && Object.keys(images).length > 0) {
      body.images = images;
    }
    const response = await api.put<{ id: string; status: string }>(body);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Application deployed: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to deploy application: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export async function deployOnly(
  eventBus: EventBus,
  alias: string,
  images?: Record<string, string>,
  targetEnvironmentSlug?: string,
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  images?: Record<string, string>;
}> {
  // Canonical path (P1-019): the deploy trigger routes through POST /runs.
  // Pre-built images (if any) are resolved server-side from the last
  // completed build; the client no longer passes an images body.
  const response = await triggerPipelineRun(
    eventBus,
    alias,
    "deploy",
    targetEnvironmentSlug,
  );
  return {
    jobId: response.sagaId,
    status: response.status,
    systemAlias: response.systemAlias,
    images,
  };
}

/** Response of the canonical POST /applications/:alias/runs command (P1-019). */
export interface PipelineRunIntentResponse {
  sagaId: string;
  pipelineRunId: string;
  status: string;
  systemAlias: string;
  sha: string | null;
  environment: string;
  pipelineMode: string;
  intent: string;
  replayed: boolean;
}

/** Generate a stable per-intent idempotency key (secure-context aware). */
function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Trigger a pipeline run through the canonical idempotent command endpoint.
 * Each call generates a fresh Idempotency-Key representing one logical user
 * intent; transport-level retries of the same logical click should reuse the
 * key. Conflicts (409) surface "a run is already in progress".
 *
 * P1-021: the intent vocabulary collapsed to `release` and `deploy`. The
 * legacy `build_and_deploy`, `build`, and `retry_build` intents are retired
 * (410 Gone). In-run retry of a failed run is expressed via `retryOf`, which
 * references the sagaId of the run to retry.
 */
export async function triggerPipelineRun(
  eventBus: EventBus,
  alias: string,
  intent: "release" | "deploy" = "release",
  targetEnvironmentSlug?: string,
  retryOf?: string,
  branch?: string,
): Promise<PipelineRunIntentResponse> {
  try {
    debugLogger.info(
      "application-api",
      `Triggering pipeline run (${intent}${retryOf ? `, retryOf=${retryOf}` : ""}${branch ? `, branch=${branch}` : ""}) for: ${alias}`,
    );

    const api = new API(
      apiBaseUrls.BACKEND,
      `/api/v1/applications/${encodeURIComponent(alias)}/runs`,
    );
    api.updateConfigHeaders({ "Idempotency-Key": generateIdempotencyKey() });
    const body: {
      intent: "release" | "deploy";
      targetEnvironmentSlug?: string;
      retryOf?: string;
      branch?: string;
    } = { intent };
    if (targetEnvironmentSlug) {
      body.targetEnvironmentSlug = targetEnvironmentSlug;
    }
    if (retryOf) {
      body.retryOf = retryOf;
    }
    if (branch) {
      body.branch = branch;
    }
    const response = await api.post<PipelineRunIntentResponse>(body);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_DEPLOYED, {
      applicationAlias: alias,
    } as ApplicationEventPayload);

    debugLogger.info(
      "application-api",
      `Pipeline run ${response.sagaId} (${response.status}${response.replayed ? ", replayed" : ""})`,
    );
    return response;
  } catch (error) {
    const errorMessage = `Failed to trigger pipeline run: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

function toLegacyShape(response: PipelineRunIntentResponse): {
  jobId: string;
  status: string;
  systemAlias: string;
  sha: string;
} {
  return {
    jobId: response.sagaId,
    status: response.status,
    systemAlias: response.systemAlias,
    sha: response.sha ?? "",
  };
}

export async function buildAndDeploy(
  eventBus: EventBus,
  alias: string,
  targetEnvironmentSlug?: string,
  branch?: string,
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  sha: string;
}> {
  return toLegacyShape(
    await triggerPipelineRun(
      eventBus,
      alias,
      "release",
      targetEnvironmentSlug,
      undefined,
      branch,
    ),
  );
}

/**
 * Retry a failed run (P1-021). Re-triggers the original intent with `retryOf`
 * set to the sagaId of the run being retried. The server replays the failed
 * run as a new attempt of the same logical run.
 */
export async function retryRun(
  eventBus: EventBus,
  alias: string,
  sagaId: string,
  intent: "release" | "deploy" = "release",
): Promise<{
  jobId: string;
  status: string;
  systemAlias: string;
  sha: string;
}> {
  return toLegacyShape(
    await triggerPipelineRun(eventBus, alias, intent, undefined, sagaId),
  );
}

export async function forceFailBuild(
  eventBus: EventBus,
  alias: string,
): Promise<{ success: boolean }> {
  try {
    debugLogger.info("application-api", `Force-failing build for: ${alias}`);

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.FORCE_FAIL_BUILD.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const response = await api.put<{ success: boolean }>({});

    debugLogger.info("application-api", `Build force-failed: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to force-fail build: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

/**
 * List repository branches for the run dialog branch picker (D3).
 * Returns branch names sorted with `main` first.
 */
export async function listBranches(
  alias: string,
): Promise<{ branches: string[] }> {
  try {
    const api = new API(
      apiBaseUrls.BACKEND,
      `/api/v1/applications/${encodeURIComponent(alias)}/branches`,
    );
    return await api.get<{ branches: string[] }>();
  } catch (error) {
    debugLogger.error(
      "application-api",
      `Failed to list branches: ${alias}`,
      error,
    );
    return { branches: [] };
  }
}
